import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Modal, Popconfirm, Space, Spin, Tag, Typography, message } from "antd";
import { DeleteOutlined, DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";

import {
    deleteTransactionReceiptFile,
    downloadTransactionReceiptFile,
    viewTransactionReceiptFile,
} from "../../api/transactionReceiptApi";
import { saveBlob } from "../transactionSchedule/fileActions";
import { formatFileSize, formatReceiptAmount, formatReceiptDate } from "./transactionReceiptUi";
import { useTranslation } from "../../i18n/LanguageProvider";

const { Text } = Typography;

/**
 * Shows the PDF receipt of one Гүйлгээний Баримт row inside a modal, without leaving the list
 * page, and offers the original file for download.
 *
 * Viewing and downloading are open to every role that may see the row. Removing the PDF is
 * rendered only for the MANAGE roles and rejected by the backend for anyone else.
 */
function TransactionReceiptDocumentModal({ open, record, canDelete = false, onClose, onDeleted }) {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");
    const [blobUrl, setBlobUrl] = useState("");

    const receiptId = record?.id;

    const loadReceipt = useCallback(async (id) => {
        setLoading(true);
        setError("");

        try {
            const result = await viewTransactionReceiptFile(id);
            const url = window.URL.createObjectURL(result.blob);
            setBlobUrl(url);
        } catch (err) {
            setError(err?.message || t("receipt.loadFailed"));
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => {
        if (!open || !receiptId) return undefined;

        loadReceipt(receiptId);

        return undefined;
    }, [open, receiptId, loadReceipt]);

    // The object URL lives as long as the modal shows that file.
    useEffect(() => {
        if (!blobUrl) return undefined;

        return () => {
            window.URL.revokeObjectURL(blobUrl);
        };
    }, [blobUrl]);

    useEffect(() => {
        if (!open) {
            setBlobUrl("");
            setError("");
        }
    }, [open]);

    const handleDownload = async () => {
        if (!receiptId) return;

        setDownloading(true);

        try {
            const result = await downloadTransactionReceiptFile(receiptId);
            saveBlob(result, record?.receiptFileName || `receipt-${receiptId}.pdf`);
            message.success(t("receipt.downloaded"));
        } catch (err) {
            message.error(err?.message || t("receipt.downloadFailed"));
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (!receiptId) return;

        setDeleting(true);

        try {
            await deleteTransactionReceiptFile(receiptId);
            message.success(t("receipt.deleted"));
            onDeleted?.(receiptId);
            onClose?.();
        } catch (err) {
            message.error(err?.message || t("receipt.deleteFailed"));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Modal
            title={
                <Space>
                    <FilePdfOutlined style={{ color: "#cf1322" }} />
                    <span>{t("receipt.modalTitle")}</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            width={960}
            destroyOnClose
            footer={
                <Space>
                    <Button onClick={onClose}>{t("action.close")}</Button>

                    {canDelete && record?.hasReceipt && (
                        <Popconfirm
                            title={t("receipt.deleteConfirmTitle")}
                            description={t("receipt.deleteConfirmBody")}
                            okText={t("action.delete")}
                            cancelText={t("action.cancel")}
                            okButtonProps={{ danger: true }}
                            onConfirm={handleDelete}
                        >
                            <Button danger icon={<DeleteOutlined />} loading={deleting}>
                                {t("receipt.delete")}
                            </Button>
                        </Popconfirm>
                    )}

                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        loading={downloading}
                        disabled={!record?.hasReceipt}
                        onClick={handleDownload}
                    >
                        {t("action.download")}
                    </Button>
                </Space>
            }
        >
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {/*
                  * A compact header rather than a bordered Descriptions grid: the file leads, its
                  * details sit under it as one muted line, and the row it belongs to reads as
                  * name - amount x rate.
                  */}
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                            <FilePdfOutlined
                                style={{ fontSize: 22, color: "#cf1322", marginTop: 2 }}
                            />
                            <div className="min-w-0">
                                <div
                                    className="truncate font-semibold text-slate-900"
                                    title={record?.receiptFileName || ""}
                                >
                                    {record?.receiptFileName || "-"}
                                </div>
                                <div className="mt-1 text-xs text-slate-500">
                                    {[
                                        formatFileSize(record?.receiptFileSize),
                                        formatReceiptDate(record?.receiptUploadedAt),
                                    ]
                                        .filter((part) => part && part !== "-")
                                        .join("  ·  ")}
                                </div>
                            </div>
                        </div>

                        <Tag color="blue" className="shrink-0" style={{ marginInlineEnd: 0 }}>
                            {`${t("col.id")} ${record?.id ?? "-"}`}
                        </Tag>
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-3">
                        <span className="min-w-0 truncate text-sm text-slate-900" title={record?.name || ""}>
                            {record?.name || "-"}
                        </span>
                        <Text type="secondary" className="text-sm">
                            {`${formatReceiptAmount(record?.amount)} ${record?.currency || ""}`.trim()}
                        </Text>
                    </div>
                </div>

                {error && (
                    <Alert type="error" showIcon message={t("receipt.error")} description={error} />
                )}

                <div
                    style={{
                        border: "1px solid #f0f0f0",
                        borderRadius: 10,
                        background: "#fafafa",
                        height: 620,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                    }}
                >
                    {loading && <Spin tip={t("receipt.loading")} />}

                    {!loading && blobUrl && (
                        <iframe
                            title="transaction-receipt-document"
                            src={blobUrl}
                            style={{ width: "100%", height: "100%", border: "none" }}
                        />
                    )}

                    {!loading && !blobUrl && !error && (
                        <Text type="secondary">{t("receipt.empty.file")}</Text>
                    )}
                </div>
            </Space>
        </Modal>
    );
}

export default TransactionReceiptDocumentModal;
